import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cx } from './cx.ts';
import { TagGroup } from './DataTag.tsx';
import s from './ItineraryRow.module.css';
import t from './type.module.css';

/** C-04 Itinerary row (docs/design/components/C-04-itinerary-row.md). */
export function ItineraryRow({
  time,
  zone,
  icon: Icon,
  title,
  subline,
  stopId,
  state = 'normal',
  variant = 'standard',
  tags,
  money,
  accessory,
}: {
  time?: string;
  zone?: string;
  icon: LucideIcon;
  title: string;
  subline?: string;
  stopId?: string;
  state?: 'normal' | 'now' | 'past';
  /** `logged`: a quick-logged expense (outline chip, money under the title). `sub`: a sub-item. */
  variant?: 'standard' | 'logged' | 'sub';
  /** At most two data tags (C-02). */
  tags?: ReactNode[];
  money?: ReactNode;
  /** A trailing tag in place of the stop ID (`MINE` on a personal expense). */
  accessory?: ReactNode;
}) {
  const timeBlock = (className?: string) =>
    variant === 'sub' ? (
      <span className={className} />
    ) : (
      <span className={className}>
        <span className={t.readoutM}>{time ?? '—'}</span>
        {zone && <span className={cx(t.micro, s.zone)}>{zone}</span>}
      </span>
    );
  return (
    <article
      className={cx(s.row, state === 'now' && s.now, state === 'past' && s.past)}
      aria-current={state === 'now' ? 'time' : undefined}
    >
      {timeBlock(s.time)}
      <span className={s.chip} aria-hidden="true">
        <Icon strokeWidth={2.5} />
      </span>
      <div className={s.text}>
        {timeBlock(s.inlineTime)}
        <h4 className={cx(s.title, variant === 'standard' ? t.titleM : t.bodyS)}>{title}</h4>
        {subline && <span className={cx(s.subline, t.caption)}>{subline}</span>}
        {tags && tags.length > 0 && (
          <span className={s.tags}>
            <TagGroup>{tags.slice(0, 2)}</TagGroup>
          </span>
        )}
        {variant === 'logged' && money}
      </div>
      <div className={s.side}>
        {accessory ?? (stopId && <span className={cx(t.readoutS, s.stop)}>{stopId}</span>)}
        {variant !== 'logged' && money}
      </div>
    </article>
  );
}

/** The leg between two rows: mode + time + distance; "last known" offline (F-02). */
export function Leg({ icon: Icon, text }: { icon: LucideIcon; text: string }) {
  return (
    <div className={cx(s.leg, t.readoutS)}>
      <span className={s.legLine} aria-hidden="true" />
      <Icon aria-hidden="true" />
      <span>{text}</span>
    </div>
  );
}
